import React, { useId } from 'react';
import Modal, { ModalSize } from './Modal';
import Button from './Button';

export interface FormModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  /** Called on submit. Wire the dialog's own <form> to this, not a button onClick. */
  onSubmit: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  description?: string;
  submitLabel?: string;
  cancelLabel?: string;
  /** Disables submit and shows the spinner in the button. */
  isSubmitting?: boolean;
  /** Independent of isSubmitting — an invalid form is not a busy one. */
  submitDisabled?: boolean;
  /** Use `danger` for a destructive confirm. */
  submitVariant?: 'primary' | 'danger';
  /**
   * True once the user has typed. Locks Escape and backdrop dismissal so a stray
   * click cannot discard their work; the Cancel and close buttons still work.
   */
  isDirty?: boolean;
  /**
   * Non-scrolling band under the header — see Modal's `subheader`. It sits
   * OUTSIDE the <form>, so Enter in a search field there cannot submit.
   */
  subheader?: React.ReactNode;
  /**
   * Extra controls pinned to the LEFT of the footer — a back button in a wizard,
   * a destructive "Delete" alongside "Save". Keeping them out of the right-hand
   * pair preserves cancel/confirm as the last thing in the tab order.
   */
  footerLeft?: React.ReactNode;
  /** Drop the Cancel button when the close affordance is the only way out. */
  hideCancel?: boolean;
}

/**
 * A dialog you DO something in. Paired with <HelpModal>, which is one you read.
 *
 * The two differ in the ways that matter to a user rather than in decoration:
 * this one is wider, keeps its actions pinned in a footer, and refuses to be
 * dismissed by accident once there is unsaved input. HelpModal is narrow, has
 * nothing to lose, and closes however you like.
 *
 * Submission goes through a real <form>, so Enter submits and browser validation
 * fires — both of which are lost when a dialog wires its primary button to an
 * onClick instead.
 */
const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  title,
  onClose,
  onSubmit,
  children,
  size = 'lg',
  description,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  submitDisabled = false,
  submitVariant = 'primary',
  isDirty = false,
  subheader,
  footerLeft,
  hideCancel = false,
}) => {
  // useId, not a constant: two FormModals mounted at once (a confirm on top of a
  // form) would otherwise share an id, and the outer form would swallow the
  // inner dialog's submit.
  const formId = useId();

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      description={description}
      onClose={onClose}
      size={size}
      subheader={subheader}
      dismissLocked={isDirty && !isSubmitting}
      footer={
        <>
          {footerLeft && <div className="mr-auto flex items-center gap-2">{footerLeft}</div>}
          {!hideCancel && (
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {cancelLabel}
            </Button>
          )}
          <Button
            type="submit"
            form={formId}
            variant={submitVariant}
            loading={isSubmitting}
            disabled={submitDisabled || isSubmitting}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      {/*
        The submit button lives in the sticky footer, outside this form in the DOM,
        so it is associated by `form={formId}` rather than by nesting. That keeps
        the footer pinned while the fields scroll, without losing Enter-to-submit.
      */}
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        {children}
      </form>
    </Modal>
  );
};

export default FormModal;
